import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component } from "@eastsideco/salvo-ts";
import { type RangeFacet, type FacetRange } from "@apptus/esales-api";
import { formatPriceString } from "@/utils/formatting";
import noUiSlider, { type API } from 'nouislider';
import 'nouislider/dist/nouislider.css';
import { SimpleEvent } from "@eastsideco/salvo-ts";

@Component
export class VoyadoPriceFilters extends ThemeComponent {
    private _range?: RangeFacet;
    private ranges: FacetRange[] = [
        { min: 0, max: 50 },
        { min: 51, max: 100 },
        { min: 101, max: 200 }
    ];
    private min: number = 0;
    private max: number = 300;
    private slider?: API;
    private _isActive = false;
    private _onChange = new SimpleEvent<FacetRange>();

    public get onChange() {
        return this._onChange.asEvent();
    }

    public get isActive() {
        return this._isActive;
    }

    get initialMin() {
        return Math.floor(this.range?.min || 0);
    }

    get initialMax() {
        return Math.ceil(this.range?.max || 300);
    }

    set range(range: RangeFacet | undefined) {
        this._range = range;
        this._isActive = false;

        this.min = this.initialMin;
        this.max = this.initialMax;

        this.innerHTML = this.renderHTML();
        this.initSlider();
        this.addCheckboxListeners();
    }

    get range() {
        return this._range;
    }

    get selectedRange(): FacetRange {
        return {
            min: this.min,
            max: this.max,
        };
    }

    get rangeOptionsHTML() {
        return this
            .ranges
            .map((range) => {
                const rangeArray = Object.values(range).filter((v) => v !== undefined);
                const rangeId = rangeArray.join('-');
                const rangeLabel = rangeArray.map((v) => formatPriceString(v.toString())).join('-');

                return `
                  <li class="collection-filters__group-options__list-item">
                    <div class="form-checkbox flex f-vertical-center">
                      <input 
                        data-ref="priceRangeCheckbox"
                        type="checkbox"
                        name="price-range"
                        id="price-range--${rangeId}"
                        value="${rangeId}">
                      
                      <label class="form-checkbox__label" for="price-range--${rangeId}">
                        ${rangeLabel}
                        <span class="checkmark"></span>
                      </label>
                    </div>
                  </li>
                `;
            })
            .join('');
    }

    renderHTML() {
        return `
          <div class="collection-filters__group-options__price-ranges">
            <ul class="collection-filters__group-options__list" data-ref="priceRangeCheckboxesContainer">
              ${this.rangeOptionsHTML}
            </ul>

            <div class="price-filters-slider" data-ref="priceRangeSliderContainer">
              <div class="price-filters-slider__wrapper">
                <div data-ref="priceRangeSlider"></div>
                <div class="price-filters-slider__values">
                  <div class="price-filters-slider__value">
                    <span data-ref="sliderValueMin" data-value="0">
                      ${formatPriceString(` ${this.min}`)}
                    </span>
                  </div>
                  
                  <div class="price-filters-slider__value">
                    <span data-ref="sliderValueMax" data-value="100">
                      ${formatPriceString(` ${this.max}`)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
    }

    initSlider() {
        const priceRangeSlider = this.querySelector('[data-ref=priceRangeSlider]');
        const sliderValueMin = this.querySelector('[data-ref=sliderValueMin]');
        const sliderValueMax = this.querySelector('[data-ref=sliderValueMax]');

        if (! priceRangeSlider) {
            return;
        }

        this.slider = noUiSlider.create(priceRangeSlider as HTMLElement, {
            start: [this.initialMin, this.initialMax],
            connect: true,
            step: 1,
            range: {
                min: this.initialMin,
                max: this.initialMax
            }
        });

        this.slider.on('slide.one', () => {
            if (!this.slider) {
                return;
            }

            // Get data- and determine lower/upper values
            const data = this.slider.get();
            const lowerValue = parseInt(Object.values(data)[0], 10);
            const lower = lowerValue.toString();
            const upperValue = parseInt(Object.values(data)[1], 10);
            const upper = upperValue.toString();

            this.min = lowerValue;
            this.max = upperValue;

            // Update visible inputs
            if (sliderValueMin) {
                sliderValueMin.innerHTML = formatPriceString(` ${lower}`);
            }

            if (sliderValueMax) {
                sliderValueMax.innerHTML = formatPriceString(` ${upper}`);
            }

            const options = this.querySelectorAll<HTMLInputElement>(
                '[data-ref=priceRangeCheckbox]'
            );

            for (const option of [...options].filter((option) => option.checked)) {
                option.checked = false;
            }
        });

        this.slider.on('end.one', () => {
            this._isActive = true;
            this._onChange.dispatch(this.selectedRange);
        });
    }

    addCheckboxListeners() {
        this.$listen(this, 'click', (event) => {
            const target = event.target as HTMLInputElement;

            if (!target?.matches('[data-ref=priceRangeCheckbox]')) {
                return;
            }

            if (!target.checked) {
                this.clearPrice();
                return;
            }

            const options = this.querySelectorAll<HTMLInputElement>(
                '[data-ref=priceRangeCheckbox]'
            );

            for (const option of [...options].filter((option) => option.checked)) {
                if (!option.isSameNode(target)) {
                    option.checked = false;
                }
            }

            const values = target.value.split('-').map((value) => Number(value));

            this.min = values[0];
            this.max = values[1];

            this.slider?.set([this.min, this.max]);

            this._isActive = true;

            this.updateSliderLabels();

            this._onChange.dispatch(this.selectedRange);
        });
    }

    private clearPrice() {
        this._isActive = false;
        this.min = this.initialMin;
        this.max = this.initialMax;

        const options = this.querySelectorAll<HTMLInputElement>(
            '[data-ref=priceRangeCheckbox]'
        );

        for (const option of options) {
            option.checked = false;
        }

        this.slider?.set([this.min, this.max]);
        this.updateSliderLabels();

        this._onChange.dispatch(this.selectedRange);
    }

    private updateSliderLabels() {
        const sliderValueMin = this.querySelector('[data-ref=sliderValueMin]');
        const sliderValueMax = this.querySelector('[data-ref=sliderValueMax]');

        if (sliderValueMin) {
            sliderValueMin.innerHTML = formatPriceString(` ${this.min}`);
        }

        if (sliderValueMax) {
            sliderValueMax.innerHTML = formatPriceString(` ${this.max}`);
        }
    }
}
